import React, { useCallback } from 'react';

// Import types
import type { EventBlock, EventByUser } from '../../types/schedule';

// Import utils
import { normalizeSegments } from '../../utils/persistence';

/**
 * Helpers for manipulating availability segments (EventBlock[]) per user.
 *
 * Non-visual, pure data logic focused on same-day segments using quarter-hour
 * indices. Keeps the storage shape identical to the SchedulePage.
 *
 * NOTE: This hook intentionally avoids timezone math. All operations are
 * performed on the provided dateKey + index ranges. Any ISO conversion remains
 * in higher-level utils if/when needed.
 */
export default function useEventsByUser(
  eventsByUser: EventByUser,
  setEventsByUser: React.Dispatch<React.SetStateAction<EventByUser>>,
  /**
   * Optional guard to enforce that only locally owned users (selfNames) are mutable.
   * If omitted, all users are considered editable (legacy behavior).
   */
  canEditUser?: (user: string) => boolean,
) {
  const getUserSegments = useCallback(
    (user: string): EventBlock[] => {
      return (eventsByUser[user] || []).slice();
    },
    [eventsByUser],
  );

  const replaceUserEvents = useCallback(
    (user: string, next: EventBlock[]) => {
      setEventsByUser((prev) => ({ ...prev, [user]: next }));
    },
    [setEventsByUser],
  );

  const addSegment = useCallback(
    (user: string, dateKey: string, startIndex: number, endIndex: number) => {
      if (!user) return;
      if (endIndex <= startIndex) return;
      if (canEditUser && !canEditUser(user)) return; // data-layer guard: edit only owned users
      setEventsByUser((prev) => {
        const cur = prev[user] || [];
        const next = normalizeSegments(
          cur.concat([
            { id: crypto.randomUUID(), dateKey, startIndex, endIndex },
          ]),
        );
        return { ...prev, [user]: next };
      });
    },
    [normalizeSegments, setEventsByUser, canEditUser],
  );

  const eraseAt = useCallback(
    (user: string, dateKey: string, rowIndex: number) => {
      if (!user) return;
      if (canEditUser && !canEditUser(user)) return; // data-layer guard
      setEventsByUser((prev) => {
        const cur = (prev[user] || []).slice();
        const out: EventBlock[] = [];
        for (const seg of cur) {
          if (seg.dateKey !== dateKey) {
            out.push(seg);
            continue;
          }
          if (rowIndex < seg.startIndex || rowIndex >= seg.endIndex) {
            out.push(seg);
            continue;
          }
          // Split the segment around rowIndex (remove just that cell)
          if (rowIndex > seg.startIndex) {
            out.push({ ...seg, endIndex: rowIndex });
          }
          if (rowIndex + 1 < seg.endIndex) {
            out.push({
              ...seg,
              id: crypto.randomUUID(),
              startIndex: rowIndex + 1,
            });
          }
        }
        return { ...prev, [user]: out };
      });
    },
    [setEventsByUser, canEditUser],
  );

  return {
    getUserSegments,
    replaceUserEvents,
    addSegment,
    eraseAt,
  };
}
